import React, { useState } from "react";

import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  InputChangeEvent,
  IScheduledTaskPropertyRef,
} from "components/pages/scheduled-task-airflow/components/InputFields";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { T } from "services/lang/LanguageService";

interface ITaskConfigIndexedDatasetTableProps extends IScheduledTaskPropertyRef {
  header: string;
  datasets: string[];
  onChange: (e: InputChangeEvent<string[]>) => void;
  bodyClass?: string;
}

interface IDatasetRowModel {
  value: string | undefined;
  editValue: string | undefined;
  editMode: boolean;
}

export default function TaskConfigIndexedDatasetTable(props: ITaskConfigIndexedDatasetTableProps) {
  const [dataArr, setDataArr] = useState<IDatasetRowModel[]>(mapToList(props.datasets));

  const propagateChangesStateUp = (arr: IDatasetRowModel[]) => {
    const tmp = { property: props.property, value: arr.map(e => e.value!!) } as InputChangeEvent<
      string[]
    >;
    props.onChange(tmp);
  };

  const confirmWrapper = (event: any, confirmHandler: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.are-you-really-want-to-delete-this-dataset-mapping"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const showWarn = (warn: string) => {
    easasAlert({ summary: T("alert.invalid"), severity: "warn", detail: warn, life: 3000 });
  };

  const handleEdit = (row: IDatasetRowModel) => {
    const idx = dataArr.indexOf(row);
    dataArr[idx].editMode = true;
    dataArr[idx].editValue = dataArr[idx].value;
    setDataArr([...dataArr]);
  };

  const handleEditCancel = (row: IDatasetRowModel) => {
    const idx = dataArr.indexOf(row);
    // Check if item is selected, if not remove row
    if (row.editValue == null || row.value == null) {
      setDataArr([...dataArr.filter(item => item.editValue != null)]);
      return;
    }

    dataArr[idx].editMode = false;
    dataArr[idx].editValue = dataArr[idx].value;
    setDataArr([...dataArr]);
  };

  const handleEditConfirm = (row: IDatasetRowModel) => {
    const idx = dataArr.indexOf(row);

    if (dataArr[idx].editValue === undefined) {
      showWarn(T("alert.dataset-must-be-selected"));
      return;
    }

    const duplicates = dataArr.filter(it => it.editValue === row.editValue).length;
    if (duplicates > 1) {
      showWarn(T("alert.dataset-already-exists-in-list"));
      return;
    }

    dataArr[idx].editMode = false;
    dataArr[idx].value = dataArr[idx].editValue;
    setDataArr([...dataArr]);
    propagateChangesStateUp([...dataArr]);
  };

  const handleAdd = () => {
    const updated = [...dataArr, { editMode: true } as IDatasetRowModel];
    setDataArr(updated);
  };

  const handleDelete = (row: IDatasetRowModel) => {
    const filtered = [...dataArr.filter(item => item.value !== row.value)];
    setDataArr(filtered);
    propagateChangesStateUp(filtered);
  };

  const handleValueChange = (e: any, row: IDatasetRowModel) => {
    const idx = dataArr.indexOf(row);
    dataArr[idx].editValue = e;
    setDataArr([...dataArr]);
  };

  const onRowReorder = (e: any) => {
    const updated = [...e.value];
    setDataArr(updated);
    propagateChangesStateUp(updated);
  };

  const headerTemplate = (
    <div className="flex justify-content-between">
      <h4>{props.header}</h4>
      <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleAdd()} />
    </div>
  );

  const indexBodyTemplate = (row: IDatasetRowModel) => {
    return <span>{dataArr.indexOf(row)}</span>;
  };

  const datasetBodyTemplate = (row: IDatasetRowModel) => {
    return (
      <>
        {!row.editMode && (
            <DatasetTitle identifier={row.value!!} />
        )}

        {row.editMode && (
          <DatasetDropdown value={row.editValue} onChange={e => handleValueChange(e, row)} />
        )}
      </>
    );
  };

  const actionBodyTemplate = (row: IDatasetRowModel) => {
    return (
      <>
        {!row.editMode && (
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text"
            onClick={() => handleEdit(row)}
          />
        )}

        {row.editMode && (
          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text"
            onClick={() => handleEditConfirm(row)}
          />
        )}

        {row.editMode && (
          <Button
            icon="pi pi-times"
            className="p-button-plain p-button-rounded p-button-text"
            onClick={() => handleEditCancel(row)}
          />
        )}

        <Button
          icon="pi pi-trash"
          className="const-del-btn  p-button-rounded p-button-text p-button-danger"
          onClick={e => confirmWrapper(e, () => handleDelete(row))}
        />
      </>
    );
  };

  return (
    <>
      <DataTable
        value={dataArr}
        responsiveLayout="scroll"
        header={headerTemplate}
        width="100%"
        className={props.bodyClass}
        onRowReorder={onRowReorder}
        size="small"
        reorderableRows
      >
        <Column rowReorder style={{ width: "3em" }} />
        <Column header={T("word.index")} body={indexBodyTemplate} style={{ width: "4rem" }} />
        <Column header={T("word.dataset")} body={datasetBodyTemplate} />
        <Column body={actionBodyTemplate} bodyStyle={{ textAlign: "right", width: "12rem" }} />
      </DataTable>

      <ConfirmPopup />
    </>
  );
}

function mapToList(map: string[]): IDatasetRowModel[] {
  return map.map(item => {
    return { value: item, editValue: item, editMode: false };
  });
}
